import { logUserDataResponseError, processUpdateResponse } from './processResponse';
import { format } from 'owa-localize';
import { makePatchRequest } from 'owa-ows-gateway';
import {
    KeyValuePair,
    UpdateUserDataItemCategoryRequest,
    UpdateUserDataItemKeyValueRequest,
    UpdateUserDataPropertyRequest,
    UserDataInfoProperty,
    UserDataStoreRequestStatus,
} from '../data/schema/UserDataStoreSchema';

const USER_DATA_STORE_UPDATE_PIN: string = 'ows/v1.5/UserDataStore/Pin';
const USER_DATA_STORE_UPDATE_BASE: string = 'ows/v1.5/UserDataStore/update/{0}';
const UPDATE_CATEGORY_URL = 'category';
const UPDATE_KEY_VALUE_URL = 'keyValue';
const UPDATE_CATEGORY_ACTION_NAME = 'UpdateUserDataCategory';
const UPDATE_KEY_VALUE_ACTION_NAME = 'UpdateUserDataValue';
const UPDATE_KEY_VALUE_PROPERTY_ACTION_NAME = 'UpdateUserDataValueProperty';

/**
 * Update the category of a UserDataStore Item.
 * @param category: The original category for the Item to update.
 * @param newCategory: The new category name.
 */
export async function updateUserDataCategoryService(
    category: string,
    newCategory: string
): Promise<UserDataStoreRequestStatus> {
    const apiUrl = format(USER_DATA_STORE_UPDATE_BASE, UPDATE_CATEGORY_URL);
    const request: UpdateUserDataItemCategoryRequest = {
        category: category,
        newCategory: newCategory,
    };
    try {
        const response = await makePatchRequest(
            apiUrl,
            request,
            undefined /* correlationId */,
            true /* returnFullResponse */
        );
        return processUpdateResponse(response, UPDATE_CATEGORY_ACTION_NAME);
    } catch (error) {
        logUserDataResponseError(UPDATE_CATEGORY_ACTION_NAME, error?.message);
        return Promise.resolve(UserDataStoreRequestStatus.UNEXPECTED_ERROR);
    }
}

/**
 * Update a specific KeyValue pair of a UserDataStore Item.
 * @param category: The category for the Item to update.
 * @param key: The key of the specific KeyValue pair to update.
 * @param newKeyValue: New KeyValue for the Item.
 */
export async function updateUserDataKeyValueService(
    category: string,
    key: string,
    newKeyValue: KeyValuePair,
    timeUTC: string
): Promise<UserDataStoreRequestStatus> {
    const apiUrl = format(USER_DATA_STORE_UPDATE_BASE, UPDATE_KEY_VALUE_URL);
    const request: UpdateUserDataItemKeyValueRequest = {
        category: category,
        key: key,
        newKeyValue: newKeyValue,
    };
    try {
        const response = await makePatchRequest(
            apiUrl,
            request,
            undefined /* correlationId */,
            true /* returnFullResponse */
        );
        return processUpdateResponse(response, UPDATE_KEY_VALUE_ACTION_NAME);
    } catch (error) {
        logUserDataResponseError(UPDATE_KEY_VALUE_ACTION_NAME, error?.message);
        return Promise.resolve(UserDataStoreRequestStatus.UNEXPECTED_ERROR);
    }
}

/**
 * Update the property of a UserDataInfo of a UserDataStore Item.
 * @param category: The category for the Item to update.
 * @param key: The key of the specific UserDataInfo to update.
 * @param newProperty: New UserDataInfoProperty for the data.
 */
export async function updateUserDataPropertyService(
    category: string,
    key: string,
    newProperty: UserDataInfoProperty
): Promise<UserDataStoreRequestStatus> {
    const request: UpdateUserDataPropertyRequest = {
        category: category,
        key: key,
        isPinned: newProperty.isPinned,
        updatedDateTimeUTC: newProperty.lastModifiedTimeUTC,
    };
    try {
        const response = await makePatchRequest(
            USER_DATA_STORE_UPDATE_PIN,
            request,
            undefined /* correlationId */,
            true /* returnFullResponse */
        );
        return processUpdateResponse(response, UPDATE_KEY_VALUE_PROPERTY_ACTION_NAME);
    } catch (error) {
        logUserDataResponseError(UPDATE_KEY_VALUE_PROPERTY_ACTION_NAME, error?.message);
        return Promise.resolve(UserDataStoreRequestStatus.UNEXPECTED_ERROR);
    }
}
